import { fse, isCloudSite, isStaticSite } from '../../electron-modules';
import { isValidURL } from './utilities';

interface ByteRange {
  first: number
  last: number
}

export const toDataURL = async (src: string): Promise<string> => {
  return new Promise(resolve => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    };

    image.src = src;
  });
};

export const readFileJSON = (path: string): Promise<Object> => {
  if (isStaticSite || isCloudSite || isValidURL(path)) {
    return readJsonFromURL(path);
  } else {
    return readJsonElectron(path);
  }
};

export const readFileText = (path: string): Promise<string> => {
  if (isStaticSite || isCloudSite || isValidURL(path)) {
    return readTextFromURL(path);
  } else {
    return readTextElectron(path);
  }
};

export const readFileBuffer = (path: string, byteRange: ByteRange = null) => {
  if (isStaticSite || isCloudSite || isValidURL(path)) {
    return readBufferFromURL(path, byteRange);
  } else {
    return readBufferElectron(path, byteRange);
  }
};

const readJsonFromURL = async (path) => {
  let headers = {
    'Content-Type': 'application/json'
  };

  const response = await fetch(path, {headers});

  if ((response.status === 404) || (response.status === 403)) {
    throw new Error("File Not Found");
  }

  const data = await response.json();
  return data;
};

const readJsonElectron = async (path) => {
  const data = await fse.readFile(path, 'utf-8');
  return JSON.parse(data);
};

const readTextFromURL = async (path) => {
  let headers = {
    'Content-Type': 'text/html; charset=utf-8'
  };

  const response = await fetch(path, {headers});

  if ((response.status === 404) || (response.status === 403)) {
    throw new Error("File Not Found");
  }

  const data = await response.text();
  return data;
};

const readTextElectron = async (path) => {
  return await fse.readFile(path, 'utf-8');
};

const readBufferFromURL = async (path, byteRange: ByteRange) : Promise<ArrayBuffer> => {
  let headers = {};

  if (byteRange) {
    const first = byteRange.first;
    const last = byteRange.last - 1;
    headers['Content-Type'] = 'multipart/byteranges';
    headers['Range'] = `bytes=${first}-${last}`;
  }

  const response = await fetch(path, {headers});

  if ((response.status === 404) || (response.status === 403)) {
    throw new Error("File Not Found");
  }

  const data = await response.arrayBuffer();

  if (byteRange) {
    const bytesRequested = byteRange.last - byteRange.first;
    if (data.byteLength !== bytesRequested) {
      throw new Error("Invalid number of bytes read");
    }
  }

  if (data.byteLength === 0) {
    throw new Error("File Not Found");
  }

  return data;
};

const readBufferElectron = async (path, byteRange: ByteRange) : Promise<Buffer> => {
  if (byteRange) {
    const byteLength = byteRange.last - byteRange.first;
    const tmpBuffer = Buffer.alloc(byteLength);

    const file = await fse.open(path, "r");
    const {buffer, bytesRead} = await fse.read(
      file, tmpBuffer, 0, byteLength, byteRange.first);
    fse.close(file);

    if (bytesRead === 0) {
      throw new Error("No bytes read");
    }

    return buffer.buffer;
  } else {
    const data = await fse.readFile(path);
    return data.buffer;
  }
};
